import React from 'react';

// The tenant's inline-image counting rule (UI_ATTACHMENTS_COUNT_INLINE), applied to the AGGREGATE
// the backend measured rather than to individual attachment rows — every list-row paperclip site
// (TicketRowMeta, UploadsSeite, DashboardSeite) has only hasAttachments + documentAttachmentCount to
// work from, never the per-attachment rows the detail pane's countAttachments (attachmentOcrStatus.ts)
// consumes. One function for all three sites so the rule can't drift between them the way the three
// paperclip glyphs already had. `?? (hasAttachments ? 1 : 0)` covers both a bundle running against a
// backend that doesn't serve the count yet AND a row stamped before the count existed — both read as
// "keep today's glyph", never as an invented absence. The deliberate, documented consequence: a
// pre-stamp row's documentAttachmentCount (when the field DOES exist on an old row) equals its total
// attachment count, inline images included — so on a flag-OFF tenant that legacy row keeps its
// paperclip even though its detail pane already says "Keine Dokumente beigefügt". Fail-open by
// design; decays as those tickets archive. Do not "fix" this into a disappearing paperclip.
export const shouldShowAttachmentGlyph = (
  ticket: { hasAttachments?: boolean; documentAttachmentCount?: number },
  attachmentsCountInline: boolean,
): boolean => attachmentsCountInline
  ? !!ticket.hasAttachments
  : (ticket.documentAttachmentCount ?? (ticket.hasAttachments ? 1 : 0)) > 0;

// The SUPERSET of shouldShowAttachmentGlyph: true for every row that could still end up with a
// paperclip under EITHER posture, false for rows no counting rule can ever give one. It exists to
// scope the reservation below — a row with no attachments at all must reserve NOTHING, because
// reserving width it will never use makes the settle drop that width plus its flex gap, which is
// the very content shift the slot was added to prevent, on the majority of rows.
export const mayShowAttachmentGlyph = (
  ticket: { hasAttachments?: boolean; documentAttachmentCount?: number },
): boolean => !!ticket.hasAttachments || (ticket.documentAttachmentCount ?? 0) > 0;

// Reserves the paperclip's footprint during the pre-settle config-loading window (cm.configLoading)
// so nothing shifts when shouldShowAttachmentGlyph's verdict becomes trustworthy — painting a glyph
// and correcting it on settle is a content shift (Layout-Stability). Render it ONLY behind
// mayShowAttachmentGlyph. `size` is caller-supplied and must equal the PaperclipIcon size that site
// actually paints (13 in TicketRowMeta, 12 in the two page-level list rows) — a slot wider than its
// glyph shifts on settle in exactly the direction it was meant to prevent.
export const AttachmentGlyphSlot: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <span data-testid="attachment-glyph-slot" style={{ width: size, flexShrink: 0 }} aria-hidden="true" />
);
